import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Upload, Image as ImageIcon, Check, Link, AlertCircle, Sparkles, X, Search, Video, Bell, Star, Tag, FileSpreadsheet, Download, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category-specific stock images from Pexels
const CATEGORY_IMAGES = {
  Books: [
    'https://images.pexels.com/photos/1907785/pexels-photo-1907785.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/733852/pexels-photo-733852.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/159711/books-bookstore-book-reading-159711.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/1148399/pexels-photo-1148399.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Crystals: [
    'https://images.pexels.com/photos/6766258/pexels-photo-6766258.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/5737625/pexels-photo-5737625.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/1573241/pexels-photo-1573241.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Incense: [
    'https://images.pexels.com/photos/4207892/pexels-photo-4207892.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/4207893/pexels-photo-4207893.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/3059892/pexels-photo-3059892.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Soaps: [
    'https://images.pexels.com/photos/3737600/pexels-photo-3737600.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/3737585/pexels-photo-3737585.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Toys: [
    'https://images.pexels.com/photos/3661193/pexels-photo-3661193.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/4226902/pexels-photo-4226902.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/163036/mario-luigi-yoschi-figures-163036.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Miscellaneous: [
    'https://images.pexels.com/photos/3270223/pexels-photo-3270223.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/6766247/pexels-photo-6766247.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
};

const DEFAULT_IMAGE = 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg?auto=compress&cs=tinysrgb&w=600';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toggingSold, setToggingSold] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Image handling state
  const [imagePreview, setImagePreview] = useState('');
  const [imageError, setImageError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Bulk upload state
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const bulkFileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    subcategory_id: '',
    image_url: '',
    video_url: '',
    affiliate_url: '',
    stock: 999999,
    is_bestseller: false,
    is_new_arrival: false,
    is_sold: false,
    notify_subscribers: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, subcatRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/book-subcategories`),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSubcategories(subcatRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const isBookCategory = () => {
    const selectedCat = categories.find(c => c.id === formData.category_id);
    return selectedCat?.name?.toLowerCase() === 'books';
  };

  const getSelectedCategoryName = () => {
    const selectedCat = categories.find(c => c.id === formData.category_id);
    return selectedCat?.name || 'Miscellaneous';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    if (name === 'image_url') {
      setImagePreview(value);
      setImageError(false);
    }
  };

  // Handle file upload to Cloudinary (persistent cloud storage)
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload JPG, PNG, GIF, or WEBP image');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    // Show immediate local preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result || '');
    };
    reader.readAsDataURL(file);

    // Upload to Cloudinary via backend
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      // Get token from localStorage for explicit auth
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Cloudinary returns the full CDN URL
      const imageUrl = response.data.url;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      setImagePreview(imageUrl);
      setImageError(false);
      toast.success('Image uploaded to cloud storage!');
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed. Please try again.';
      toast.error(errorMsg);
      // Clear preview on error
      setImagePreview('');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Auto-assign category image (fallback option)
  const useAutoImage = () => {
    const categoryName = getSelectedCategoryName();
    const images = CATEGORY_IMAGES[categoryName] || CATEGORY_IMAGES.Miscellaneous;
    const randomImage = images[Math.floor(Math.random() * images.length)];
    setFormData((prev) => ({ ...prev, image_url: randomImage }));
    setImagePreview(randomImage);
    setImageError(false);
    toast.success(`Auto-assigned ${categoryName} stock image`);
  };

  const clearImage = () => {
    setFormData((prev) => ({ ...prev, image_url: '' }));
    setImagePreview('');
    setImageError(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImagePreviewError = () => {
    setImageError(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      subcategory_id: '',
      image_url: '',
      video_url: '',
      affiliate_url: '',
      stock: 999999,
      is_bestseller: false,
      is_new_arrival: false,
      notify_subscribers: false,
    });
    setEditingProduct(null);
    setImagePreview('');
    setImageError(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
        category_id: product.category_id,
        subcategory_id: product.subcategory_id || '',
        image_url: product.image_url || '',
        video_url: product.video_url || '',
        affiliate_url: product.affiliate_url || '',
        stock: product.stock || 999999,
        is_bestseller: product.is_bestseller || false,
        is_new_arrival: product.is_new_arrival || false,
        is_sold: product.is_sold || false,
        notify_subscribers: false,
      });
      setImagePreview(product.image_url || '');
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || null,
        image_url: formData.image_url || '',
        video_url: formData.video_url || '',
        affiliate_url: formData.affiliate_url || '',
        stock: parseInt(formData.stock) || 999999,
        is_bestseller: formData.is_bestseller,
        is_new_arrival: formData.is_new_arrival,
        is_sold: formData.is_sold,
        notify_subscribers: formData.notify_subscribers,
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload);
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success('Product created successfully!');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    setDeleting(productId);
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleSold = async (productId, currentSoldStatus) => {
    setToggingSold(productId);
    try {
      const response = await axios.put(`${API}/admin/products/${productId}/sold`);
      toast.success(response.data.message);
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, is_sold: response.data.is_sold } : p
      ));
    } catch (error) {
      toast.error('Failed to update sold status');
    } finally {
      setToggingSold(null);
    }
  };

  // Bulk upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/products/bulk-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'brookzone_products_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setBulkUploading(true);
    setBulkResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/products/bulk-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setBulkResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`Successfully imported ${response.data.success_count} products!`);
        fetchData(); // Refresh product list
      }
      
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} rows had errors. See details below.`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Bulk upload failed';
      toast.error(errorMsg);
      setBulkResults({ error: errorMsg });
    } finally {
      setBulkUploading(false);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = '';
      }
    }
  };

  const resetBulkUpload = () => {
    setBulkResults(null);
    setIsBulkUploadOpen(false);
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (product.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category_id === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-products">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-foreground">Products</h2>
          <p className="text-muted-foreground">{products.length} products in store</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setIsBulkUploadOpen(true)}
            data-testid="bulk-upload-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
          <Button onClick={() => openDialog()} className="btn-accent" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog open={isBulkUploadOpen} onOpenChange={setIsBulkUploadOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Bulk Upload Products
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Instructions */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium">How to bulk upload:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Download the template file</li>
                <li>Fill in your product data (name, price, category are required)</li>
                <li>Save as CSV or Excel format</li>
                <li>Upload your file below</li>
              </ol>
              
              <div className="pt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDownloadTemplate}
                  data-testid="download-template-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </div>

            {/* Column Reference */}
            <div className="text-sm">
              <h4 className="font-medium mb-2">Column Reference:</h4>
              <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                <div><Badge variant="secondary">name</Badge> <span className="text-red-500">*</span></div>
                <div><Badge variant="secondary">price</Badge> <span className="text-red-500">*</span></div>
                <div><Badge variant="secondary">category</Badge> <span className="text-red-500">*</span></div>
                <div><Badge variant="outline">description</Badge></div>
                <div><Badge variant="outline">subcategory</Badge> <span className="text-xs">(for Books)</span></div>
                <div><Badge variant="outline">image_url</Badge></div>
                <div><Badge variant="outline">video_url</Badge></div>
                <div><Badge variant="outline">affiliate_url</Badge></div>
                <div><Badge variant="outline">is_bestseller</Badge> <span className="text-xs">(yes/no)</span></div>
                <div><Badge variant="outline">is_new_arrival</Badge> <span className="text-xs">(yes/no)</span></div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                <span className="text-red-500">*</span> Required fields. Valid categories: Books, Crystals, Incense, Soaps, Toys, Miscellaneous
              </p>
            </div>

            {/* Upload Area */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={bulkFileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleBulkUpload}
                className="hidden"
                id="bulk-file-input"
                data-testid="bulk-file-input"
              />
              
              {bulkUploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  <p className="text-muted-foreground">Processing your file...</p>
                </div>
              ) : (
                <label htmlFor="bulk-file-input" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="h-10 w-10 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Click to upload or drag and drop</p>
                      <p className="text-sm text-muted-foreground">CSV or Excel files (.csv, .xlsx, .xls)</p>
                    </div>
                  </div>
                </label>
              )}
            </div>

            {/* Results */}
            {bulkResults && (
              <div className="space-y-4">
                {bulkResults.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">{bulkResults.error}</p>
                  </div>
                ) : (
                  <>
                    {/* Summary */}
                    <div className="flex gap-4">
                      <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-green-700">{bulkResults.success_count}</p>
                        <p className="text-sm text-green-600">Imported</p>
                      </div>
                      <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                        <XCircle className="h-6 w-6 text-red-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-red-700">{bulkResults.error_count}</p>
                        <p className="text-sm text-red-600">Errors</p>
                      </div>
                    </div>

                    {/* Error Details */}
                    {bulkResults.errors && bulkResults.errors.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                        <h4 className="font-medium text-red-700 mb-2">Errors:</h4>
                        <ul className="space-y-1 text-sm">
                          {bulkResults.errors.map((err, i) => (
                            <li key={i} className="text-red-600">
                              Row {err.row}: {err.name ? `"${err.name}" - ` : ''}{err.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Success Details */}
                    {bulkResults.success && bulkResults.success.length > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-32 overflow-y-auto">
                        <h4 className="font-medium text-green-700 mb-2">Imported:</h4>
                        <ul className="text-sm text-green-600 space-y-0.5">
                          {bulkResults.success.slice(0, 10).map((item, i) => (
                            <li key={i}>Row {item.row}: {item.name}</li>
                          ))}
                          {bulkResults.success.length > 10 && (
                            <li className="text-muted-foreground">...and {bulkResults.success.length - 10} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetBulkUpload}>
              {bulkResults ? 'Close' : 'Cancel'}
            </Button>
            {bulkResults && bulkResults.success_count > 0 && (
              <Button onClick={() => { resetBulkUpload(); }} className="btn-accent">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredProducts.map((product) => (
          <Card key={product.id} className={`overflow-hidden ${product.is_sold ? 'ring-2 ring-red-400 bg-red-50/30' : ''}`} data-testid={`product-card-${product.id}`}>
            <div className="aspect-square relative bg-muted">
              <img
                src={product.image_url || DEFAULT_IMAGE}
                alt={product.name}
                className={`w-full h-full object-cover ${product.is_sold ? 'grayscale opacity-60' : ''}`}
                onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
              />
              {product.is_sold && (
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-600 text-white text-lg px-4 py-2 rounded-lg font-bold shadow-lg transform -rotate-12">
                  SOLD
                </span>
              )}
              {product.is_bestseller && !product.is_sold && (
                <span className="absolute top-2 left-2 bg-accent text-accent-foreground text-xs px-2 py-1 rounded-full font-medium">
                  Bestseller
                </span>
              )}
              {product.is_new_arrival && !product.is_sold && (
                <span className="absolute top-2 right-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  New
                </span>
              )}
              {product.affiliate_url && (
                <span className="absolute bottom-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Affiliate
                </span>
              )}
            </div>
            <CardContent className="p-4">
              <h3 className="font-medium text-foreground line-clamp-1">{product.name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-1">{product.category_name}</p>
              <div className="flex items-center justify-between mt-1">
                <p className={`text-lg font-bold ${product.is_sold ? 'text-muted-foreground line-through' : 'text-primary'}`}>${product.price.toFixed(2)}</p>
                {product.is_sold && (
                  <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">SOLD</span>
                )}
              </div>
              
              {/* Action Buttons - Always Visible */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openDialog(product)}>
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button 
                  size="sm" 
                  variant={product.is_sold ? "default" : "outline"}
                  className={product.is_sold ? "bg-green-600 hover:bg-green-700 text-white" : "border-orange-400 text-orange-600 hover:bg-orange-50"}
                  onClick={() => handleToggleSold(product.id, product.is_sold)}
                  disabled={toggingSold === product.id}
                >
                  {toggingSold === product.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : product.is_sold ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Restock
                    </>
                  ) : (
                    <>
                      <Tag className="h-3 w-3 mr-1" />
                      Sold
                    </>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 border-red-200"
                  onClick={() => handleDelete(product.id)}
                  disabled={deleting === product.id}
                >
                  {deleting === product.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No products found</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Image Section */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Product Image</Label>
              
              {/* Image Preview */}
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg overflow-hidden">
                {(imagePreview || formData.image_url) ? (
                  <div className="relative group">
                    <img
                      src={imagePreview || formData.image_url}
                      alt="Product preview"
                      className="w-full h-48 object-cover"
                      onError={handleImagePreviewError}
                    />
                    {/* Error overlay */}
                    {imageError && (
                      <div className="absolute inset-0 bg-red-500/20 flex flex-col items-center justify-center">
                        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
                        <span className="text-red-600 text-sm font-medium">Image failed to load</span>
                      </div>
                    )}
                    {/* Upload progress */}
                    {uploading && (
                      <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                        <Loader2 className="h-8 w-8 text-white animate-spin mb-2" />
                        <span className="text-white text-sm">{uploadProgress}%</span>
                      </div>
                    )}
                    {/* Success indicator */}
                    {formData.image_url && !imageError && !uploading && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                    {/* Hover actions */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </Button>
                      <Button type="button" variant="secondary" size="sm" onClick={useAutoImage} disabled={!formData.category_id}>
                        <Sparkles className="h-4 w-4 mr-1" />
                        Auto
                      </Button>
                      <Button type="button" variant="destructive" size="sm" onClick={clearImage}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-10 w-10 text-primary animate-spin mb-2" />
                        <span className="text-sm text-muted-foreground">Uploading to cloud... {uploadProgress}%</span>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="h-12 w-12 mx-auto text-primary" />
                        <p className="text-sm font-medium text-foreground">Upload your product image</p>
                        <p className="text-xs text-muted-foreground">Images are stored permanently in the cloud</p>
                        <div className="flex flex-wrap justify-center gap-2">
                          <Button type="button" className="btn-accent" size="sm" onClick={() => fileInputRef.current?.click()}>
                            <Upload className="h-4 w-4 mr-1" />
                            Choose Image
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={useAutoImage} disabled={!formData.category_id}>
                            <Sparkles className="h-4 w-4 mr-1" />
                            Use Stock Image
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">Or paste an image URL below</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                className="hidden"
              />
              
              {/* Image URL input */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg or Cloudinary URL"
                    className="pl-10"
                  />
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={useAutoImage} disabled={!formData.category_id} title="Use stock image">
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
              
              {formData.image_url && formData.image_url.includes('cloudinary') && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Image stored in cloud - will persist across deployments
                </p>
              )}
            </div>

            {/* Product Details */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* MARK AS SOLD - Prominent at top */}
              <div className="space-y-2 sm:col-span-2">
                <div className="flex items-center justify-between bg-red-50 p-4 rounded-lg border-2 border-red-300">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <Tag className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <Label htmlFor="is_sold" className="text-red-800 text-base font-semibold">Mark as Sold</Label>
                      <p className="text-sm text-red-600">Item will show as unavailable to customers</p>
                    </div>
                  </div>
                  <Switch
                    id="is_sold"
                    checked={formData.is_sold || false}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_sold: checked }))}
                    className="scale-125"
                  />
                </div>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter product name"
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter product description"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Price ($) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="stock">Stock</Label>
                <Input
                  id="stock"
                  name="stock"
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={handleInputChange}
                  placeholder="100"
                />
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, category_id: value, subcategory_id: '' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isBookCategory() && (
                <div className="space-y-2">
                  <Label>Subcategory</Label>
                  <Select
                    value={formData.subcategory_id}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, subcategory_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="bestseller">Mark as Bestseller</Label>
                  <Switch
                    id="bestseller"
                    checked={formData.is_bestseller}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_bestseller: checked }))}
                  />
                </div>
              </div>
              
              {/* New Arrival Toggle */}
              <div className="space-y-2 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    <Label htmlFor="new_arrival">Mark as New Arrival</Label>
                  </div>
                  <Switch
                    id="new_arrival"
                    checked={formData.is_new_arrival}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_new_arrival: checked }))}
                  />
                </div>
                <p className="text-xs text-muted-foreground">New arrivals are displayed in a special section on the homepage</p>
              </div>
              
              {/* Notify Subscribers (only for new products) */}
              {!editingProduct && formData.is_new_arrival && (
                <div className="space-y-2 sm:col-span-2">
                  <div className="flex items-center justify-between bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 text-amber-600" />
                      <div>
                        <Label htmlFor="notify" className="text-amber-800">Notify Subscribers</Label>
                        <p className="text-xs text-amber-600">Send email to all subscribers about this new arrival</p>
                      </div>
                    </div>
                    <Switch
                      id="notify"
                      checked={formData.notify_subscribers}
                      onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, notify_subscribers: checked }))}
                    />
                  </div>
                </div>
              )}

              {/* Video URL */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="video_url" className="flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Video URL (optional)
                </Label>
                <Input
                  id="video_url"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleInputChange}
                  placeholder="https://res.cloudinary.com/... or YouTube/Vimeo URL"
                  data-testid="video-url-input"
                />
                <p className="text-xs text-muted-foreground">Add a product video from Cloudinary, YouTube, or Vimeo</p>
              </div>

              {/* Affiliate URL */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="affiliate_url" className="flex items-center gap-2">
                  Affiliate Link (optional)
                </Label>
                <Input
                  id="affiliate_url"
                  name="affiliate_url"
                  value={formData.affiliate_url}
                  onChange={handleInputChange}
                  placeholder="https://amazon.com/... or any partner link"
                  data-testid="affiliate-url-input"
                />
                <p className="text-xs text-muted-foreground">External purchase link (Amazon, etc.) — shown alongside Add to Cart</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="btn-accent" disabled={submitting}>
              {submitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
